// FacilitatorPortalNotice.tsx
/**
 * Inline failure state for the facilitator portal pages (My Enrolments — Active / Pending /
 * Cancelled, My Sessions, etc.).
 *
 * These endpoints 403 with "You are not registered as a facilitator" when the signed-in account holds
 * the facilitator *role* but has no Facilitator *record* behind it. That is a setup problem the user
 * can act on, so it belongs in the page body — a toast disappears and leaves an empty table that reads
 * like a bug. Mirrors the counselling LeaderPortalNotice.
 */
import { Card, Code, Stack, Text, ThemeIcon, Button } from '@mantine/core';
import { IconAlertTriangle, IconRefresh, IconUserExclamation } from '@tabler/icons-react';

/** Pull status/message off a thrown SDK `CCMSError` (or anything else) without assuming its shape. */
function readApiError(error: unknown): { status?: number; message: string } {
  const e = error as { status?: number; statusCode?: number; message?: string } | null;
  return {
    status: e?.status ?? e?.statusCode,
    message: e?.message ?? '',
  };
}

/** True when the backend says this account has no facilitator record. */
function isNotRegisteredAsFacilitator(error: unknown): boolean {
  const { status, message } = readApiError(error);
  if (status === 403) return true;
  return /not registered as a facilitator/i.test(message);
}

interface FacilitatorPortalNoticeProps {
  error: unknown;
  /** What the page was trying to load, e.g. "your enrolments" — used in the generic message. */
  subject: string;
  onRetry: () => void;
}

export default function FacilitatorPortalNotice({ error, subject, onRetry }: FacilitatorPortalNoticeProps) {
  const notAFacilitator = isNotRegisteredAsFacilitator(error);
  const detail = readApiError(error);

  return (
    <Card withBorder radius="md" padding="xl">
      <Stack align="center" gap="sm">
        <ThemeIcon size={56} radius="md" variant="light" color={notAFacilitator ? 'yellow' : 'red'}>
          {notAFacilitator ? <IconUserExclamation size={28} /> : <IconAlertTriangle size={28} />}
        </ThemeIcon>

        <Text fw={600} size="lg" ta="center">
          {notAFacilitator ? 'You are not registered as a facilitator' : `Could not load ${subject}`}
        </Text>

        <Text size="sm" c="dimmed" ta="center" maw={520}>
          {notAFacilitator ? (
            <>
              Your account has the facilitator role, but no facilitator profile is linked to it yet, so
              there are no enrolments to show. Ask the courses office to add you under
              <Text span fw={600}> Information &gt; Facilitators</Text>, then try again.
            </>
          ) : (
            <>
              Something went wrong whilst loading {subject}. This is usually temporary. Try again, and
              let the courses office know if it keeps happening.
            </>
          )}
        </Text>

        {/* The generic branch used to say only "something went wrong", which hid the one detail that
            makes a failure diagnosable. Show the status and server message when there is one. */}
        {!notAFacilitator && (detail.status || detail.message) && (
          <Code block style={{ maxWidth: 520, whiteSpace: 'pre-wrap' }}>
            {[detail.status ? `HTTP ${detail.status}` : null, detail.message || null]
              .filter(Boolean)
              .join(' — ')}
          </Code>
        )}

        <Button variant="light" leftSection={<IconRefresh size={16} />} onClick={onRetry} mt="xs">
          Try again
        </Button>
      </Stack>
    </Card>
  );
}
